import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Plus,
  Search,
  Edit2,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Play,
  XCircle,
  BarChart3,
  Calendar,
  Clock,
  Users,
  Eye,
  Radio,
  Copy,
} from 'lucide-react';
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
  Badge,
  Modal,
  ModalFooter,
  Input,
  Select,
  StatCard,
} from '@azyntor-labs/nexus-ui';
import { examsApi, usersApi } from '@/api/endpoints';
import { Exam, ExamStatus } from '@/types';
import { toast } from '@/utils/toast';
import { Link, useNavigate } from 'react-router-dom';
import { getErrorMessage, formatExamDateShort, formatExamDateTime } from '@/utils';

interface ExamStudent {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface ExamEnrollment {
  student_id: string;
  student_name: string;
}

export const ExamManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);

  // Fetch status counts (lightweight, loads first)
  const { data: statusCountsResponse } = useQuery({
    queryKey: ['exam-status-counts'],
    queryFn: () => examsApi.statusCounts(),
    staleTime: 30000,
  });

  const statusCounts = statusCountsResponse?.data?.data || {} as Record<string, number>;
  const draftCount = statusCounts.draft || 0;
  const scheduledCount = statusCounts.scheduled || 0;
  const activeCount = statusCounts.active || 0;
  const completedCount = statusCounts.completed || 0;

  // Fetch exams (paginated)
  const { data: examsResponse, isLoading } = useQuery({
    queryKey: ['exams', { page, per_page: perPage, status: statusFilter }],
    queryFn: () => examsApi.list({
      page,
      per_page: perPage,
      status: statusFilter || undefined
    }),
  });

  const exams = Array.isArray(examsResponse?.data?.data?.data) ? examsResponse.data.data.data : [];
  const totalPages = Math.ceil((examsResponse?.data?.data?.total || 0) / perPage);
  const total = examsResponse?.data?.data?.total || 0;

  // Filter exams by search term (client-side)
  const filteredExams = exams.filter((exam: Exam) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      exam.title.toLowerCase().includes(searchLower) ||
      (exam.description && exam.description.toLowerCase().includes(searchLower))
    );
  });

  // Delete exam mutation
  const deleteExamMutation = useMutation({
    mutationFn: (id: string) => examsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      toast.success('Exam deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedExam(null);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete exam'));
    },
  });

  // Publish exam mutation
  const publishExamMutation = useMutation({
    mutationFn: (id: string) => examsApi.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      toast.success('Exam published successfully');
      setIsPublishModalOpen(false);
      setSelectedExam(null);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to publish exam'));
    },
  });

  // Activate exam mutation (start now)
  const activateExamMutation = useMutation({
    mutationFn: (id: string) => examsApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      toast.success('Exam activated successfully');
      setIsActivateModalOpen(false);
      setSelectedExam(null);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to activate exam'));
    },
  });

  // Cancel exam mutation
  const cancelExamMutation = useMutation({
    mutationFn: (id: string) => examsApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      toast.success('Exam cancelled successfully');
      setIsCancelModalOpen(false);
      setSelectedExam(null);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to cancel exam'));
    },
  });

  // Fetch all students for enrollment
  const { data: studentsResponse, isLoading: isLoadingStudents, error: studentsError } = useQuery({
    queryKey: ['users', { role: 'student' }],
    queryFn: () => usersApi.list({ role: 'student', per_page: 1000 }),
    enabled: isAssignModalOpen,
    staleTime: 30000,
  });

  const allStudents: ExamStudent[] = Array.isArray(studentsResponse?.data?.data)
    ? studentsResponse.data.data
    : [];

  // Fetch enrolled students for selected exam
  const { data: enrolledResponse, refetch: refetchEnrolled } = useQuery({
    queryKey: ['enrollments', selectedExam?.id],
    queryFn: () => selectedExam ? examsApi.getEnrolledStudents(selectedExam.id) : Promise.resolve(null),
    enabled: !!selectedExam && isAssignModalOpen,
  });

  const enrolledStudents: ExamEnrollment[] = enrolledResponse?.data?.data || [];
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');

  // Enroll students mutation
  const enrollStudentsMutation = useMutation({
    mutationFn: ({ examId, studentIds }: { examId: string; studentIds: string[] }) =>
      examsApi.enrollStudents(examId, studentIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      refetchEnrolled();
      toast.success('Students assigned successfully');
      setSelectedStudentIds([]);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to assign students'));
    },
  });

  // Unenroll student mutation
  const unenrollStudentMutation = useMutation({
    mutationFn: ({ examId, studentId }: { examId: string; studentId: string }) =>
      examsApi.unenrollStudent(examId, studentId),
    onSuccess: () => {
      refetchEnrolled();
      toast.success('Student removed successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to remove student'));
    },
  });

  const handleDeleteExam = (exam: Exam) => {
    setSelectedExam(exam);
    setIsDeleteModalOpen(true);
  };

  const handleCloneExam = async (exam: Exam) => {
    try {
      // Fetch questions for this exam
      const questionsRes = await examsApi.getQuestions(exam.id);
      const questions = questionsRes?.data?.data || [];
      const questionIds = Array.isArray(questions) ? questions.map((q: any) => q.id) : [];

      // Store exam data in sessionStorage for the create page to pick up
      const cloneData = {
        title: `${exam.title} (Copy)`,
        description: exam.description,
        instructions: exam.instructions,
        duration_minutes: exam.duration || exam.duration_minutes,
        total_marks: exam.total_marks,
        passing_marks: exam.passing_marks,
        is_proctored: exam.is_proctored,
        shuffle_questions: exam.shuffle_questions,
        shuffle_options: exam.shuffle_options,
        show_results: exam.show_results,
        show_answers: exam.show_answers,
        max_attempts: exam.max_attempts,
        require_webcam: exam.require_webcam,
        require_fullscreen: exam.require_fullscreen,
        detect_tab_switch: exam.detect_tab_switch,
        detect_copy_paste: exam.detect_copy_paste,
        ai_proctoring: exam.ai_proctoring,
        allow_late_submit: exam.allow_late_submit,
        certificate_enabled: exam.certificate_enabled,
        certificate_config: exam.certificate_config,
        settings: exam.settings,
        question_ids: questionIds,
      };
      sessionStorage.setItem('exam_clone_data', JSON.stringify(cloneData));
      navigate('/teacher/exams/create');
      toast.success(`Cloning "${exam.title}" — update the details and save as new exam`);
    } catch {
      toast.error('Failed to load exam data for cloning');
    }
  };

  const handlePublishExam = (exam: Exam) => {
    setSelectedExam(exam);
    setIsPublishModalOpen(true);
  };

  const handleActivateExam = (exam: Exam) => {
    setSelectedExam(exam);
    setIsActivateModalOpen(true);
  };

  const handleCancelExam = (exam: Exam) => {
    setSelectedExam(exam);
    setIsCancelModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedExam) return;
    deleteExamMutation.mutate(selectedExam.id);
  };

  const handleConfirmPublish = () => {
    if (!selectedExam) return;
    publishExamMutation.mutate(selectedExam.id);
  };

  const handleConfirmActivate = () => {
    if (!selectedExam) return;
    activateExamMutation.mutate(selectedExam.id);
  };

  const handleConfirmCancel = () => {
    if (!selectedExam) return;
    cancelExamMutation.mutate(selectedExam.id);
  };

  const handleAssignStudents = (exam: Exam) => {
    // Navigate to dedicated enrollment page instead of opening modal
    navigate(`/teacher/exams/${exam.id}/enrollment`);
  };

  const handleToggleStudent = (studentId: string) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleConfirmAssign = () => {
    if (!selectedExam || selectedStudentIds.length === 0) return;
    enrollStudentsMutation.mutate({
      examId: selectedExam.id,
      studentIds: selectedStudentIds,
    });
  };

  const handleRemoveStudent = (studentId: string) => {
    if (!selectedExam) return;
    unenrollStudentMutation.mutate({
      examId: selectedExam.id,
      studentId,
    });
  };

  // Check if exam is currently live (published/scheduled/active + start_time passed + end_time not reached)
  const isExamLive = (exam: Exam) => {
    if (!exam.start_time) return false;
    // Only published, scheduled, or active exams can be "live" — not drafts
    if (exam.status === 'draft' || exam.status === 'completed' || exam.status === 'cancelled') return false;
    const now = new Date();
    const start = new Date(exam.start_time);
    const end = exam.end_time ? new Date(exam.end_time) : null;
    return now >= start && (!end || now <= end);
  };

  // Get effective display status (shows "live" for scheduled/published exams past start time)
  const getDisplayStatus = (exam: Exam): string => {
    if (isExamLive(exam) && (exam.status === 'scheduled' || exam.status === 'published' || exam.status === 'active')) {
      return 'live';
    }
    return exam.status;
  };

  const getStatusBadgeVariant = (status: ExamStatus | string) => {
    switch (status) {
      case 'live':
      case 'active':
        return 'success';
      case 'scheduled':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Exam Management</h1>
          <p className="text-white/50 mt-1">Create and manage your exams</p>
        </div>
        <Link to="/teacher/exams/create">
          <Button leftIcon={<Plus className="w-4 h-4" />}>
            Create Exam
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        <StatCard title="Total" value={statusCounts.total || 0} icon={<FileText className="w-4 h-4" />} color="blue" className="!p-3 [&_h3]:!text-xl" />
        <StatCard title="Draft" value={draftCount} icon={<FileText className="w-4 h-4" />} color="purple" className="!p-3 [&_h3]:!text-xl" />
        <StatCard title="Published" value={statusCounts.published || 0} icon={<Eye className="w-4 h-4" />} color="teal" className="!p-3 [&_h3]:!text-xl" />
        <StatCard title="Scheduled" value={scheduledCount} icon={<Clock className="w-4 h-4" />} color="orange" className="!p-3 [&_h3]:!text-xl" />
        <StatCard title="Active" value={activeCount} icon={<TrendingUp className="w-4 h-4" />} color="green" className="!p-3 [&_h3]:!text-xl" />
        <StatCard title="Completed" value={completedCount} icon={<BarChart3 className="w-4 h-4" />} color="blue" className="!p-3 [&_h3]:!text-xl" />
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="p-4 border-b border-white/[0.06]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search exams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            />
            <div className="flex items-center text-sm text-white/50">
              <Filter className="w-4 h-4 mr-2" />
              Showing {filteredExams.length} of {total} exams
            </div>
          </div>
        </div>

        {/* Exams Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-white/60">Loading exams...</p>
          </div>
        ) : filteredExams.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Title</TableHeader>
                  <TableHeader>Start Date</TableHeader>
                  <TableHeader>Duration</TableHeader>
                  <TableHeader>Points</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExams.map((exam: Exam) => (
                  <TableRow key={exam.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium text-white">{exam.title}</div>
                        <div className="text-sm text-white/50 line-clamp-1">
                          {exam.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-white/60">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatExamDateShort(exam.start_time)}
                      </div>
                    </TableCell>
                    <TableCell className="text-white/60">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {exam.duration || exam.duration_minutes} min
                      </div>
                    </TableCell>
                    <TableCell className="text-white/60">
                      {exam.total_marks || exam.total_points} pts
                    </TableCell>
                    <TableCell>
                      {getDisplayStatus(exam) === 'live' ? (
                        <Badge variant="success" size="sm">
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                            LIVE
                          </span>
                        </Badge>
                      ) : (
                        <Badge variant={getStatusBadgeVariant(exam.status)} size="sm">
                          {exam.status}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1 flex-wrap gap-1">
                        <Link to={`/teacher/exams/${exam.id}/view`}>
                          <Button variant="ghost" size="sm" leftIcon={<Eye className="w-3 h-3" />}>
                            View
                          </Button>
                        </Link>
                        <Link to={`/teacher/exams/${exam.id}/edit`}>
                          <Button variant="ghost" size="sm" leftIcon={<Edit2 className="w-3 h-3" />}>
                            Edit
                          </Button>
                        </Link>
                        <Link to={`/analytics/exams/${exam.id}`}>
                          <Button variant="ghost" size="sm" leftIcon={<BarChart3 className="w-3 h-3" />}>
                            Analytics
                          </Button>
                        </Link>
                        {(exam.status === 'draft' || exam.status === 'scheduled') && !isExamLive(exam) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Play className="w-3 h-3" />}
                            onClick={() => handlePublishExam(exam)}
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                          >
                            Publish
                          </Button>
                        )}
                        {(exam.status === 'scheduled' || exam.status === 'published') && !isExamLive(exam) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Play className="w-3 h-3 fill-current" />}
                            onClick={() => handleActivateExam(exam)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          >
                            Start Now
                          </Button>
                        )}
                        {isExamLive(exam) && (
                          <Link to={`/teacher/live-monitor`}>
                            <Button
                              variant="ghost"
                              size="sm"
                              leftIcon={<Radio className="w-3 h-3" />}
                              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            >
                              Live
                            </Button>
                          </Link>
                        )}
                        {(exam.status === 'scheduled' || exam.status === 'active' || exam.status === 'published') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Users className="w-3 h-3" />}
                            onClick={() => handleAssignStudents(exam)}
                            className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                          >
                            Assign
                          </Button>
                        )}
                        {(exam.status === 'scheduled' || exam.status === 'active') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<XCircle className="w-3 h-3" />}
                            onClick={() => handleCancelExam(exam)}
                            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Copy className="w-3 h-3" />}
                          onClick={() => handleCloneExam(exam)}
                          className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                        >
                          Clone
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Trash2 className="w-3 h-3" />}
                          onClick={() => handleDeleteExam(exam)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="p-4 border-t border-white/[0.06] flex items-center justify-between">
              <div className="text-sm text-white/50">
                Page {page} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-white/20 mx-auto mb-2" />
            <p className="text-white/40 mb-3">No exams found</p>
            {searchTerm ? (
              <p className="text-sm text-white/30">Try adjusting your search or filters</p>
            ) : (
              <Link to="/teacher/exams/create">
                <Button size="sm" leftIcon={<Plus className="w-4 h-4" />}>
                  Create Your First Exam
                </Button>
              </Link>
            )}
          </div>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Exam"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-white">{selectedExam?.title}</span>? This action cannot be undone.
          </p>
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-red-300">
              Warning: All attempts and data associated with this exam will be permanently deleted.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleteExamMutation.isPending}
          >
            {deleteExamMutation.isPending ? 'Deleting...' : 'Delete Exam'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Publish Confirmation Modal */}
      <Modal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        title="Publish Exam"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Are you sure you want to publish{' '}
            <span className="font-semibold text-white">{selectedExam?.title}</span>?
          </p>
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <p className="text-sm text-emerald-300">
              Once published, the exam will be available to students at the scheduled start time.
            </p>
          </div>
          {selectedExam && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Start Time:</span>
                <span className="font-medium text-white">
                  {formatExamDateTime(selectedExam.start_time)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">End Time:</span>
                <span className="font-medium text-white">
                  {formatExamDateTime(selectedExam.end_time)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Duration:</span>
                <span className="font-medium text-white">{selectedExam.duration || selectedExam.duration_minutes} minutes</span>
              </div>
            </div>
          )}
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsPublishModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmPublish}
            disabled={publishExamMutation.isPending}
          >
            {publishExamMutation.isPending ? 'Publishing...' : 'Publish Exam'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        title="Cancel Exam"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Are you sure you want to cancel{' '}
            <span className="font-semibold text-white">{selectedExam?.title}</span>?
          </p>
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-sm text-amber-300">
              This will prevent students from accessing the exam. Existing attempts will not be affected.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsCancelModalOpen(false)}>
            Close
          </Button>
          <Button
            variant="warning"
            onClick={handleConfirmCancel}
            disabled={cancelExamMutation.isPending}
          >
            {cancelExamMutation.isPending ? 'Cancelling...' : 'Cancel Exam'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Activate Confirmation Modal */}
      <Modal
        isOpen={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        title="Start Exam Now"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Are you sure you want to start{' '}
            <span className="font-semibold text-white">{selectedExam?.title}</span> immediately?
          </p>
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-300 mb-2">
              <strong>This will:</strong>
            </p>
            <ul className="text-sm text-blue-300 list-disc list-inside space-y-1">
              <li>Override the scheduled start time</li>
              <li>Make the exam immediately available to students</li>
              <li>Start the exam duration countdown</li>
            </ul>
          </div>
          {selectedExam?.start_time && (
            <div className="text-sm text-white/60">
              <p>Scheduled start: <span className="font-medium text-white">{formatExamDateTime(selectedExam.start_time)}</span></p>
              <p className="mt-1">New start: <span className="font-medium text-blue-400">Now</span></p>
            </div>
          )}
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsActivateModalOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmActivate}
            disabled={activateExamMutation.isPending}
          >
            {activateExamMutation.isPending ? 'Starting...' : 'Start Now'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Assign Students Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        title="Assign Students"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Assign students to <span className="font-semibold text-white">{selectedExam?.title}</span>
          </p>

          {/* Currently Enrolled Students */}
          {enrolledStudents.length > 0 && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-300 mb-2">
                Currently Enrolled ({enrolledStudents.length})
              </h4>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {enrolledStudents.map((enrollment) => (
                  <div
                    key={enrollment.student_id}
                    className="flex items-center gap-2 px-2 py-1 bg-white/5 border border-blue-500/30 rounded text-xs text-white/80"
                  >
                    <span>{enrollment.student_name}</span>
                    <button
                      onClick={() => handleRemoveStudent(enrollment.student_id)}
                      className="text-red-400 hover:text-red-300"
                      disabled={unenrollStudentMutation.isPending}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Student Selection */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-2">Select Students to Assign</h4>

            {/* Search Input */}
            <div className="mb-3">
              <Input
                type="text"
                placeholder="Search students..."
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>

            <div className="border border-white/[0.06] rounded-lg max-h-64 overflow-y-auto">
              {isLoadingStudents ? (
                <div className="p-4 text-center text-white/50">Loading students...</div>
              ) : studentsError ? (
                <div className="p-4 text-center text-red-400">
                  Failed to load students. Please try again.
                </div>
              ) : allStudents.length === 0 ? (
                <div className="p-4 text-center text-white/50">
                  No students available.
                </div>
              ) : (
                <div className="divide-y divide-white/[0.06]">
                  {allStudents
                    .filter((student) =>
                      !enrolledStudents.some((e) => e.student_id === student.id)
                    )
                    .filter((student) => {
                      const search = studentSearchTerm.toLowerCase();
                      return (
                        student.first_name?.toLowerCase().includes(search) ||
                        student.last_name?.toLowerCase().includes(search) ||
                        student.email?.toLowerCase().includes(search)
                      );
                    })
                    .map((student) => (
                      <label
                        key={student.id}
                        className="flex items-center gap-3 p-3 hover:bg-white/[0.02] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedStudentIds.includes(student.id)}
                          onChange={() => handleToggleStudent(student.id)}
                          className="w-4 h-4 text-teal-500 border-white/20 rounded bg-white/5 focus:ring-teal-500/20 focus:ring-offset-0"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-white">
                            {student.first_name} {student.last_name}
                          </div>
                          <div className="text-xs text-white/50">{student.email}</div>
                        </div>
                      </label>
                    ))}
                </div>
              )}
            </div>
            {selectedStudentIds.length > 0 && (
              <p className="text-sm text-white/60 mt-2">
                {selectedStudentIds.length} student(s) selected
              </p>
            )}
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setIsAssignModalOpen(false);
              setSelectedStudentIds([]);
              setStudentSearchTerm('');
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleConfirmAssign}
            disabled={selectedStudentIds.length === 0 || enrollStudentsMutation.isPending}
          >
            {enrollStudentsMutation.isPending ? 'Assigning...' : `Assign ${selectedStudentIds.length} Student(s)`}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
